import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bookmark, BookmarkPlus, Trash2, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

// Shared (company-wide) saved report views: report + period + chart type + filters.
export default function SavedViewsMenu({ token, current, onApply, isAdmin, userId }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [views, setViews] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => axios.get(`${API}/reports/v2/views`, auth).then(({ data }) => setViews(data.items || [])).catch(() => setViews([]));
  useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const save = async () => {
    const name = window.prompt('Name this view (shared with everyone who can see Reports):', current.suggestedName || '');
    if (!name?.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/reports/v2/views`, { name: name.trim(), ...current.payload }, auth);
      await load();
      toast({ title: 'View saved', description: `"${name.trim()}" is now available to your team.` });
    } catch (e) { toast({ title: 'Could not save view', description: e?.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete the saved view "${v.name}"?`)) return;
    try { await axios.delete(`${API}/reports/v2/views/${v.id}`, auth); await load(); }
    catch (e) { toast({ title: 'Could not delete', description: e?.response?.data?.detail || e.message, variant: 'destructive' }); }
  };

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium" style={{ background: '#1A2433', color: '#fff', border: '1px solid #2D3748' }}
        data-testid="saved-views-btn">
        <Bookmark className="w-3.5 h-3.5" /> Views{views.length ? ` (${views.length})` : ''}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-72 rounded-xl shadow-xl z-30 p-2" style={{ background: '#0F1923', border: '1px solid #2D3748' }} data-testid="saved-views-menu">
          <button type="button" onClick={save} disabled={busy}
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-semibold text-left hover:bg-white/5 disabled:opacity-60" style={{ color: '#00C896' }} data-testid="save-view-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookmarkPlus className="w-3.5 h-3.5" />} Save current view…
          </button>
          <div className="my-1 border-t" style={{ borderColor: '#2D3748' }} />
          {views.length === 0 && <p className="px-2.5 py-2 text-xs" style={{ color: '#64748B' }}>No saved views yet.</p>}
          <div className="max-h-64 overflow-y-auto">
            {views.map((v) => (
              <div key={v.id} className="flex items-center gap-1 rounded-lg hover:bg-white/5" data-testid={`saved-view-${v.id}`}>
                <button type="button" onClick={() => { setOpen(false); onApply(v); }} className="flex-1 text-left px-2.5 py-2 min-w-0" data-testid={`apply-view-${v.id}`}>
                  <p className="text-xs text-white font-medium truncate">{v.name}</p>
                  <p className="text-[10px] truncate" style={{ color: '#64748B' }}>{v.report_key} · {v.preset === 'custom' && v.start_date ? `${v.start_date} → ${v.end_date}` : (v.preset || 'default')}{v.chart_view && v.chart_view !== 'table' ? ` · ${v.chart_view}` : ''} · {v.created_by_name}</p>
                </button>
                {(isAdmin || v.created_by === userId) && (
                  <button type="button" onClick={() => remove(v)} className="p-1.5 rounded hover:bg-red-500/10" title="Delete view" data-testid={`delete-view-${v.id}`}>
                    <Trash2 className="w-3.5 h-3.5" style={{ color: '#F87171' }} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
